import { editHeaders, recordEditRevision, editResult, waitForEditLock } from './edit-sessions';
import { noteResponse } from './maintenance';
import type { LoginChallenge } from '@mcpanel/core';
import type {
  AccessToken,
  AuditEntry,
  BuildInfo,
  ChangeRequest,
  RequestKind,
  Snapshot,
  SoftwareFamily,
  SoftwareInfo,
  VersionInfo,
  ConsoleEvent,
  Invite,
  Job,
  Me,
  MetricWindow,
  MetricsResponse,
  NodeStatus,
  Role,
  ServerView,
  TokenScope,
  User,
} from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly jobId?: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

export interface Transport {
  request<T>(method: string, path: string, body?: unknown): Promise<T>;
  /** バイナリをそのまま送る（ファイルのアップロード） */
  upload<T>(path: string, body: Blob): Promise<T>;
  openConsole(serverId: string, onEvent: (event: ConsoleEvent) => void): () => void;
}

const BASE = '/api/v1';

export const httpTransport: Transport = {
  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    Object.assign(headers, editHeaders(path, body, method));
    if (method !== 'GET') headers['X-MCPanel-CSRF'] = '1';
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const response = await fetch(BASE + path, {
      method,
      headers,
      credentials: 'same-origin',
      keepalive: path === '/locks/release',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    noteResponse(response);
    if (response.status === 204) return undefined as T;
    const data: unknown = await response.json().catch(() => undefined);
    if (!response.ok) {
      if (response.status === 409) editResult(path, body, true);
      const error = data as
        { error?: string; jobId?: string; code?: string; issues?: Array<{ message?: string }> } | undefined;
      // 入力エラー（400）は項目ごとの理由を添えて、何を直せばよいか分かるようにする。
      const reasons = (error?.issues ?? []).map((i) => i.message).filter(Boolean);
      const message = reasons.length ? `入力内容を確かめてください: ${reasons.join(' / ')}` : error?.error;
      throw new ApiError(response.status, message ?? `HTTP ${response.status}`, error?.jobId, error?.code);
    }
    if (method === 'GET')
      recordEditRevision(path, response.headers.get('X-Edit-Revision'), response.headers.get('X-Edit-Content-Hash'));
    else if (!path.startsWith('/locks')) editResult(path, body, false);
    return data as T;
  },
  async upload<T>(path: string, body: Blob): Promise<T> {
    // 書き込みと同じく、その画面の編集ロックを持っていればトークンを付ける（Velocity のファイルなど）
    const response = await fetch(BASE + path, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-MCPanel-CSRF': '1',
        'Content-Type': 'application/octet-stream',
        ...editHeaders(path, undefined, 'POST'),
      },
      credentials: 'same-origin',
      body,
    });
    noteResponse(response);
    const data: unknown = await response.json().catch(() => undefined);
    if (!response.ok) {
      if (response.status === 409) editResult(path, undefined, true);
      const error = data as { error?: string } | undefined;
      throw new ApiError(response.status, error?.error ?? `HTTP ${response.status}`);
    }
    editResult(path, undefined, false);
    return data as T;
  },
  openConsole(serverId, onEvent) {
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${location.host}${BASE}/servers/${encodeURIComponent(serverId)}/console/stream`;
    let socket: WebSocket | undefined;
    let closed = false;
    let retry = 1000;
    const connect = () => {
      socket = new WebSocket(url);
      socket.onopen = () => {
        retry = 1000;
      };
      socket.onmessage = (message) => {
        try {
          onEvent(JSON.parse(String(message.data)) as ConsoleEvent);
        } catch {
          // 壊れたメッセージは無視する（表示を止めない）
        }
      };
      socket.onclose = () => {
        if (closed) return;
        setTimeout(connect, retry);
        retry = Math.min(retry * 2, 30000);
      };
    };
    connect();
    return () => {
      closed = true;
      socket?.close();
    };
  },
};

let transport: Transport = httpTransport;

export function setTransport(next: Transport) {
  transport = next;
}

async function requestWithLock<T>(method: string, path: string, body?: unknown): Promise<T> {
  await waitForEditLock(method, path, body);
  return transport.request<T>(method, path, body);
}

const get = <T>(path: string) => requestWithLock<T>('GET', path);
const post = <T>(path: string, body?: unknown) => requestWithLock<T>('POST', path, body ?? {});
const patch = <T>(path: string, body: unknown) => requestWithLock<T>('PATCH', path, body);
const del = (path: string) => requestWithLock<void>('DELETE', path);
const id = (value: string) => encodeURIComponent(value);
const windowQuery = (period: MetricWindow) =>
  new URLSearchParams(typeof period === 'string' ? { range: period } : period).toString();

type EnrollmentInput = import('@mcpanel/core').EnrollmentInput;
type EnrollmentOptions = import('@mcpanel/core').EnrollmentOptions;
type EnrollmentResult = import('@mcpanel/core').EnrollmentResult;
type StepUpResult = import('@mcpanel/core').StepUpResult;

export const api = {
  clientPackChecks: () => get<import('@mcpanel/core').ClientPackCheckSummary[]>('/client-pack-checks'),
  searchModpacks: (q: string, source: 'modrinth' | 'curseforge' = 'modrinth') =>
    get<import('@mcpanel/core').ModSearchHit[]>(`/modpacks/search?source=${source}&q=${encodeURIComponent(q)}`),
  modpackVersions: (projectId: string, source: 'modrinth' | 'curseforge' = 'modrinth') =>
    get<Awaited<ReturnType<import('@mcpanel/core').ModrinthClient['modpackVersions']>>>(
      `/modpacks/versions?source=${source}&projectId=${encodeURIComponent(projectId)}`,
    ),
  selectModpack: (projectId: string, versionId: string, source: 'modrinth' | 'curseforge' = 'modrinth') =>
    post<import('@mcpanel/core').ModpackPreview>('/modpacks/select', { source, projectId, versionId }),
  uploadModpackJar: (id: string, projectId: string, file: Blob) =>
    transport.upload<import('@mcpanel/core').ModpackPreview>(`/modpacks/${id}/jars/${projectId}`, file),
  uploadModpack: (file: Blob) => transport.upload<import('@mcpanel/core').ModpackPreview>('/modpacks/upload', file),
  serverModpack: (serverId: string) =>
    get<{
      identity: import('@mcpanel/core').ModpackDeployment['identity'];
      conflicts: string[];
      update?: { id: string; number: string };
    } | null>(`/servers/${id(serverId)}/modpack`),
  mcpPolicy: () => get<import('@mcpanel/core').McpPolicy>('/settings/mcp'),
  homepage: () => get<import('@mcpanel/core').HomepageSettings>('/settings/homepage'),
  setHomepage: (settings: import('@mcpanel/core').HomepageSettings) =>
    requestWithLock<import('@mcpanel/core').HomepageSettings>('PUT', '/settings/homepage', settings),
  homepageImages: () => get<import('@mcpanel/core').HomepageImage[]>('/settings/homepage/images'),
  uploadHomepageImage: (target: string, file: Blob) =>
    transport.upload<import('@mcpanel/core').HomepageImage>(
      `/settings/homepage/images?target=${encodeURIComponent(target)}`,
      file,
    ),
  deleteHomepageImage: (imageId: string) => del(`/settings/homepage/images/${id(imageId)}`),
  setMcpPolicy: (policy: import('@mcpanel/core').McpPolicy) =>
    requestWithLock<import('@mcpanel/core').McpPolicy>('PUT', '/settings/mcp', policy),
  completions: (serverId: string, input: string) =>
    get<import('@mcpanel/core').Completions>(
      `/servers/${id(serverId)}/console/completions?input=${encodeURIComponent(input)}`,
    ),
  launch: (serverId: string) => get<import('@mcpanel/core').LaunchView>(`/servers/${id(serverId)}/launch`),
  worldGeneration: (serverId: string) =>
    get<import('@mcpanel/core').ServerWorldGenerationView>(`/servers/${id(serverId)}/world-generation`),
  worldGenerationOptions: () => get<import('@mcpanel/core').WorldGenerationOptions>('/world-generation-options'),
  gameSettings: (serverId: string) =>
    get<import('@mcpanel/core').GameSettings>(`/servers/${id(serverId)}/game-settings`),
  ports: (serverId: string) => get<import('@mcpanel/core').PortsView>(`/servers/${id(serverId)}/ports`),
  webSites: (serverId: string) => get<import('@mcpanel/core').WebSitesView>(`/servers/${id(serverId)}/web-sites`),
  setWebSites: (serverId: string, sites: import('@mcpanel/core').WebSite[]) =>
    requestWithLock<import('@mcpanel/core').WebSitesView>('PUT', `/servers/${id(serverId)}/web-sites`, { sites }),
  bedrock: (serverId: string) => get<import('@mcpanel/core').BedrockView>(`/servers/${id(serverId)}/bedrock`),
  me: () => get<Me>('/auth/me'),
  setupOptions: (token: string, input: EnrollmentInput) =>
    post<EnrollmentOptions>('/auth/setup/options', { token, ...input }),
  setupVerify: (token: string, code: string) => post<EnrollmentResult>('/auth/setup/verify', { token, code }),
  login: (login: string, password: string) => post<LoginChallenge>('/auth/login', { login, password }),
  verifyLogin: (code: string) => post<{ user: User }>('/auth/login/verify', { code }),
  cancelLogin: () => post<void>('/auth/login/cancel'),
  logout: () => post<void>('/auth/logout'),
  devLogin: () => post<{ user: User }>('/auth/dev-login'),
  devStepUp: () => post<StepUpResult>('/auth/dev-step-up'),
  stepUpVerify: (code: string) => post<StepUpResult>('/auth/step-up', { code }),
  changePassword: (password: string, code: string, newPassword: string) =>
    post<void>('/me/password', { password, code, newPassword }),
  totpOptions: (password: string, code: string) => post<EnrollmentOptions>('/me/totp/options', { password, code }),
  totpVerify: (code: string) => post<EnrollmentResult>('/me/totp/verify', { code }),
  recoveryStatus: () => get<import('@mcpanel/core').RecoveryStatus>('/me/recovery-codes'),
  regenerateRecovery: (password: string, code: string) =>
    post<import('@mcpanel/core').RecoveryCodesResult>('/me/recovery-codes/regenerate', { password, code }),
  resetUser: (userId: string) => post<{ url: string }>(`/users/${id(userId)}/reset`),
  resetInfo: (token: string) => get<{ login?: string; name: string }>(`/auth/reset/${id(token)}`),
  resetOptions: (token: string, input: EnrollmentInput) =>
    post<EnrollmentOptions>(`/auth/reset/${id(token)}/options`, input),
  resetVerify: (token: string, code: string) => post<EnrollmentResult>(`/auth/reset/${id(token)}/verify`, { code }),

  users: () => get<User[]>('/users'),
  updateUser: (userId: string, change: { role?: Role; disabled?: boolean }) =>
    patch<User>(`/users/${id(userId)}`, change),
  minecraftAccounts: () => get<import('@mcpanel/core').MinecraftAccount[]>('/minecraft-accounts'),
  addMinecraftAccount: (userId: string, name: string) =>
    post<import('@mcpanel/core').MinecraftAccount[]>(`/users/${id(userId)}/minecraft-accounts`, { name }),
  removeMinecraftAccount: (userId: string, uuid: string) =>
    requestWithLock<import('@mcpanel/core').MinecraftAccount[]>(
      'DELETE',
      `/users/${id(userId)}/minecraft-accounts/${id(uuid)}`,
    ),
  invites: () => get<Invite[]>('/invites'),
  createInvite: (role: Role) => post<{ invite: Invite; url: string }>('/invites', { role }),
  deleteInvite: (inviteId: string) => del(`/invites/${id(inviteId)}`),
  inviteInfo: (token: string) => get<{ role: Role; valid: boolean }>(`/invites/accept/${id(token)}`),
  inviteOptions: (token: string, input: EnrollmentInput) =>
    post<EnrollmentOptions>(`/invites/accept/${id(token)}/options`, input),
  inviteVerify: (token: string, code: string) =>
    post<EnrollmentResult>(`/invites/accept/${id(token)}/verify`, { code }),

  tokens: () => get<AccessToken[]>('/me/tokens'),
  createToken: (name: string, scopes: TokenScope[]) =>
    post<{ token: AccessToken; secret: string }>('/me/tokens', { name, scopes }),
  revokeToken: (tokenId: string) => del(`/me/tokens/${id(tokenId)}`),

  nodes: () => get<NodeStatus[]>('/nodes'),
  wakeNode: (name: string) => rawRequest<{ ok: boolean }>('POST', `/nodes/${encodeURIComponent(name)}/wake`),
  servers: () => get<ServerView[]>('/servers'),
  server: (serverId: string) => get<ServerView>(`/servers/${id(serverId)}`),
  start: (serverId: string) => post<Job>(`/servers/${id(serverId)}/start`),
  stop: (serverId: string, vm = false) => post<Job>(`/servers/${id(serverId)}/stop${vm ? '?vm=true' : ''}`),
  restart: (serverId: string) => post<Job>(`/servers/${id(serverId)}/restart`),
  console: (serverId: string, command: string) =>
    post<{ output: string }>(`/servers/${id(serverId)}/console`, { command }),
  logs: (serverId: string, tail = 200) => get<{ lines: string[] }>(`/servers/${id(serverId)}/logs?tail=${tail}`),
  metrics: (serverId: string, period: MetricWindow) =>
    get<MetricsResponse>(`/servers/${id(serverId)}/metrics?${windowQuery(period)}`),
  nodeMetrics: (name: string, period: MetricWindow) =>
    get<MetricsResponse>(`/nodes/${encodeURIComponent(name)}/metrics?${windowQuery(period)}`),
  openConsole: (serverId: string, onEvent: (event: ConsoleEvent) => void) => transport.openConsole(serverId, onEvent),

  jobs: (filter: { serverId?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (filter.serverId) query.set('serverId', filter.serverId);
    if (filter.limit) query.set('limit', String(filter.limit));
    const suffix = query.size ? `?${query}` : '';
    return get<Job[]>(`/jobs${suffix}`);
  },
  job: (jobId: string) => get<Job>(`/jobs/${id(jobId)}`),
  audit: (limit = 100, filters: import('@mcpanel/core').AuditFilter = {}) =>
    get<AuditEntry[]>(
      `/audit?limit=${limit}&${new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => typeof entry[1] === 'string')).toString()}`,
    ),

  requests: (filter: { status?: 'pending' | 'all'; serverId?: string } = {}) => {
    const query = new URLSearchParams({ status: filter.status ?? 'all' });
    if (filter.serverId) query.set('serverId', filter.serverId);
    return get<ChangeRequest[]>(`/requests?${query}`);
  },
  request: (requestId: string) => get<ChangeRequest>(`/requests/${id(requestId)}`),
  createRequest: (kind: RequestKind, params: Record<string, unknown>, serverId?: string) =>
    post<ChangeRequest>('/requests', { kind, serverId, params }),
  approveRequest: (requestId: string) => post<ChangeRequest>(`/requests/${id(requestId)}/approve`),
  rejectRequest: (requestId: string) => post<ChangeRequest>(`/requests/${id(requestId)}/reject`),

  software: () => get<SoftwareInfo[]>('/software'),
  versions: (family: SoftwareFamily, snapshots = false) =>
    get<VersionInfo[]>(`/software/${family}/versions${snapshots ? '?snapshots=true' : ''}`),
  builds: (family: SoftwareFamily, mcVersion: string) =>
    get<BuildInfo[]>(`/software/${family}/versions/${id(mcVersion)}/builds`),

  downgradeOptions: (serverId: string, target: import('./types').SoftwareSpec) =>
    get<import('@mcpanel/core').DowngradeOptions>(
      `/servers/${id(serverId)}/downgrade-options?family=${id(target.family)}&mcVersion=${id(target.mcVersion)}`,
    ),
  snapshots: (serverId: string) => get<Snapshot[]>(`/servers/${id(serverId)}/snapshots`),
  createSnapshot: (serverId: string, note?: string) => post<Job>(`/servers/${id(serverId)}/snapshots`, { note }),
};

/** 段階ごとに分けた API モジュール（client-p3.ts など）から使う */
export function rawRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  return requestWithLock<T>(method, path, body ?? (method === 'GET' ? undefined : {}));
}

export async function uploadRequest<T>(path: string, body: Blob): Promise<T> {
  await waitForEditLock('POST', path);
  return transport.upload<T>(path, body);
}
